import React from "react"
import { createPolymorphicComponent, Divider, DividerProps, Tabs, TabsProps } from "@mantine/core"
import { matchPath, To, useLocation, useNavigate } from "react-router-dom"
import { Icon } from "@iconify/react"
import { clsx } from 'clsx'
import styled from "styled-components"

import { Icons, IconNames } from "src/components/ui/Icons"

type NavTab = {
    label: string;
    icon?: IconNames;
    route: string;
}

export type NavTabsProps = {
    parentId: string;
    tabs: Record<string, NavTab>;
    urlRoot: string;
    overlay?: boolean;
}

const _StyledTabs = styled(Tabs)`
    padding: 3px;
    border-radius: var(--mantine-radius-sm);
    [data-mantine-color-scheme='light'] & {
        background-color: var(--mantine-color-gray-3);
    }
    [data-mantine-color-scheme='dark'] & {
        background-color: var(--mantine-color-dark-9);
    }
    & .mantine-Tabs-tabLabel {
        flex: none;
    }
    & .mantine-Tabs-tab {
        justify-content: center;
        font-weight: 500;
        border-radius: var(--mantine-radius-sm);
        [data-mantine-color-scheme='light'] & {
            color: var(--mantine-color-gray-6);
        }
        [data-mantine-color-scheme='dark'] & {
            color: var(--mantine-color-dark-4);
        }
        &:hover {
            [data-mantine-color-scheme='light'] & {
                color: var(--mantine-color-black);
            }
            [data-mantine-color-scheme='dark'] & {
                color: var(--mantine-color-white);
            }
        }
        &[data-active] {
            font-weight: 500;
            [data-mantine-color-scheme='light'] & {
                background-color: var(--mantine-color-gray-8); 
                color: var(--mantine-color-white);
            }
            [data-mantine-color-scheme='dark'] & {
                background-color: var(--mantine-color-dark-1); 
                color: var(--mantine-color-white);
            }
        }
    }
`
const StyledTabs = createPolymorphicComponent<'div', TabsProps>(_StyledTabs)

export function useOverlayBackground() {
    
  const location = useLocation()
  // Lock the initial background for the entire overlay session
  const initial = React.useRef<Location>(location.state?.drawerFrom ?? location)
  return initial.current
}

export function useOverlayNavigate() {
  const navigate = useNavigate()
  const bg = useOverlayBackground()
  return React.useCallback(
    (to: To, opts?: { replace?: boolean; state?: any }) => {
      navigate(to, {
        ...opts,
        state: { ...(opts?.state || {}), drawerFrom: bg }, // <- preserve
      })
    },
    [navigate, bg]
  )
}


export const NavTabs: React.FC<NavTabsProps> = ({ tabs, urlRoot, parentId, overlay = false }) => {

    const navigate = useNavigate()
    const navigateOverlay = useOverlayNavigate()
    const location = useLocation()
    const match = matchPath({ path: `${urlRoot}/:id/:page`, end: true }, location.pathname)

    let activeTab = Object.keys(tabs)[0]

    if (match?.params.page) {
        let currentTabIndex = Object.keys(tabs).indexOf(match?.params.page)
        if (currentTabIndex >= 0) {
            activeTab = Object.keys(tabs)[currentTabIndex]
        }
    }

    const onChange = (newTabValue: string|null) => {
        if (newTabValue) {
            let newTab = tabs[newTabValue]
            if (newTab) {
                if(overlay) {
                    navigateOverlay(`${urlRoot}/${parentId}/${newTab.route}`)
                } else {
                    navigate(`${urlRoot}/${parentId}/${newTab.route}`)
                }
            }
        }
    };

    if (!parentId) return null;

    return (
        <StyledTabs
            value={activeTab}
            onChange={onChange}
            variant="unstyled"
            mb="sm"
        >
            <Tabs.List grow>
                {Object.keys(tabs).map(tabKey => {
                    let tab = tabs[tabKey];
                    return <Tabs.Tab key={tabKey} value={tabKey} leftSection={tab.icon ? <Icon icon={Icons[tab.icon]} width={17} /> : null} >{tab.label}</Tabs.Tab>
                })}
            </Tabs.List>
        </StyledTabs>
    )

}

export const TabDivider: React.FC<{ label: string }> = ({ label }) => <Divider my="xs" mt={0} mb="sm" label={label} variant="dashed" labelPosition="center" />

const _StyledTabDividerStatus = styled(Divider)`
    &.invalid .mantine-Divider-label {
        color: var(--mantine-color-red-9);
        &:before, &:after {
            border-top-color: var(--mantine-color-red-9);
        }
    }
`
const StyledTabDividerStatus = createPolymorphicComponent<'div', DividerProps>(_StyledTabDividerStatus)

export const TabDividerStatus: React.FC<{ label: string, valid: boolean }> = ({ label, valid }) => (
    <StyledTabDividerStatus
        my="xs"
        mt={0}
        mb="md"
        label={label}
        variant="dashed"
        labelPosition="center"
        className={clsx({ valid: valid, invalid: !valid })}
    />
)
export const TabDividerSpacing: React.FC<{ label: string }> = ({ label }) => <Divider my="sm" label={label} variant="dashed" labelPosition="center" />